/**
 * Pre/Post Night Accordion Component
 * Displays optional pre-night or post-night accommodation
 * Accordion is closed by default
 */

import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack
} from "@mui/material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { PrePostNightAccordionProps } from "../types";

export const OPEN_NIGHT_ACCORDION_EVENT = "collette:open-night-accordion";

const PrePostNightAccordion: React.FC<PrePostNightAccordionProps> = ({
  night,
  type,
  testIdPrefix
}) => {
  const [expanded, setExpanded] = useState(false);
  const t = useTranslations("PDP");

  useEffect(() => {
    const handleOpenAccordion = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: "pre" | "post" }>;
      if (customEvent.detail?.type === type) {
        setExpanded(true);
      }
    };
    window.addEventListener(OPEN_NIGHT_ACCORDION_EVENT, handleOpenAccordion);
    return () => {
      window.removeEventListener(OPEN_NIGHT_ACCORDION_EVENT, handleOpenAccordion);
    };
  }, [type]);
  
  // Build the title based on type
  const title = `${type === "pre" ? t("Optional Pre-Night") : t("Optional Post-Night")} ${night.city}`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        backgroundColor: "background.cool_strong",
        borderRadius: "2rem!important",
        boxShadow: 0,
        "&:before": { display: "none" }
      }}
      slotProps={{
        heading: {
          component: "div"
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${type}-night-content`}
        id={`${type}-night-header`}
        data-testid={`${testIdPrefix}_pre-post-night-accordion_${type}`}
        sx={{
          padding: "2.4rem",
          minHeight: "74px",
          "&.Mui-expanded": {
            minHeight: "74px"
          },
          "& .MuiAccordionSummary-content": {
            margin: "0!important"
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            width: "100%",
            paddingRight: "1.2rem"
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: "1.8rem", md: "2rem" },
              fontWeight: 600,
              lineHeight: 1.3,
              color: "primary.main",
              margin: 0
            }}
            data-testid={`${testIdPrefix}_accordion-title`}
          >
            {title}
          </Typography>

          {night.price && (
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 700,
                lineHeight: 1.5,
                color: "primary.main"
              }}
              data-testid={`${testIdPrefix}_price`}
            >
              {`From ${night.price} pp*`}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          padding: "0 2.4rem 2.4rem"
        }}
      >
        {" "}
        <Box
          data-testid={`${testIdPrefix}_extension-day-card-container`}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", legacyDesktop: "row" },
            gap: "2.4rem",
            padding: { xs: "1.6rem", md: "2.4rem" }
            // WHY: Stack vertically on tablet/mobile (<992px), horizontal on desktop
            // WHY: Responsive padding matches other PDP components
          }}
        >
          <Box
            sx={{
              width: { legacyDesktop: "45.6rem" },
              alignSelf: { xs: "stretch", legacyDesktop: "auto" },
              height: "30.7rem",
              borderRadius: "2rem",
              overflow: "hidden",
              flexShrink: 0,
              position: "relative"
              // WHY: Consistent 30.7rem height across all breakpoints
              // WHY: Stretch to full width on mobile/tablet (<992px), fixed 45.6rem on desktop
            }}
          >
            <Image
              src={night.imageUrl}
              alt={night.imageAltTag}
              fill
              sizes="(max-width: 992px) 100vw, 456px"
              style={{ objectFit: "cover" }}
              data-testid={`${testIdPrefix}_day-card-image`}
            />
          </Box>
          <Stack gap="1.2rem" flex={1}>
            {night.accommodation && (
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
                data-testid={`${testIdPrefix}_accommodation`}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 700 }}
                  data-testid={`${testIdPrefix}_accommodation-label`}
                >
                  {t("Accommodation")}:{" "}
                </Box>
                {night.accommodation}
              </Typography>
            )}
            {night.description && (
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
                data-testid={`${testIdPrefix}_description`}
              >
                {night.description}
              </Typography>
            )}
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PrePostNightAccordion;
